'use client';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, SlidersHorizontal } from 'lucide-react';

const categories = [
  { label: 'All Categories', count: 847 },
  { label: 'Trades & Construction', count: 412 },
  { label: 'Restaurants & Cafés', count: 287 },
  { label: 'Health & Medical', count: 198 },
  { label: 'Accountants & Finance', count: 156 },
  { label: 'Estate Agents & Property', count: 203 },
  { label: 'Automotive', count: 178 },
  { label: 'Hair & Beauty', count: 244 },
  { label: 'IT & Technology', count: 143 },
  { label: 'Legal Services', count: 94 },
];

const towns = [
  'All of West Suffolk',
  'Bury St Edmunds',
  'Newmarket',
  'Haverhill',
  'Mildenhall',
  'Brandon',
  'Clare',
  'Sudbury',
  'Stowmarket',
];

export default function SearchFilters() {
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedTown, setSelectedTown] = useState('All of West Suffolk');
  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [radius, setRadius] = useState('10');
  const [catExpanded, setCatExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleTier = (tier: string) => {
    setSelectedTiers((prev) =>
      prev.includes(tier) ? prev.filter((t) => t !== tier) : [...prev, tier]
    );
  };

  const filterContent = (
    <div className="space-y-6">
      {/* Category */}
      <div>
        <button
          className="flex items-center justify-between w-full text-sm font-bold text-foreground mb-3"
          onClick={() => setCatExpanded(!catExpanded)}
        >
          Category
          {catExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
        {catExpanded && (
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
            {categories.map((cat) => (
              <button
                key={`filter-cat-${cat.label}`}
                onClick={() => setSelectedCategory(cat.label)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm transition-colors ${
                  selectedCategory === cat.label
                    ? 'bg-primary text-primary-foreground font-semibold'
                    : 'hover:bg-muted text-foreground'
                }`}
              >
                <span>{cat.label}</span>
                <span className="tabular-nums text-xs opacity-70">{cat.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <hr className="border-border" />

      {/* Location */}
      <div>
        <label className="text-sm font-bold text-foreground block mb-3">Location</label>
        <select
          value={selectedTown}
          onChange={(e) => setSelectedTown(e.target.value)}
          className="input-field text-sm mb-3"
        >
          {towns.map((town) => (
            <option key={`filter-town-${town}`} value={town}>
              {town}
            </option>
          ))}
        </select>
        <div>
          <label className="text-xs text-muted-foreground mb-1.5 block">
            Search radius: {radius} miles
          </label>
          <input
            type="range"
            min="1"
            max="30"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            className="w-full accent-primary"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>1 mi</span>
            <span>30 mi</span>
          </div>
        </div>
      </div>

      <hr className="border-border" />

      {/* Listing tier */}
      <div>
        <label className="text-sm font-bold text-foreground block mb-3">Listing Type</label>
        <div className="space-y-2">
          {[
            { id: 'tier-filter-featured', value: 'featured', label: 'Featured', color: 'text-warning' },
            { id: 'tier-filter-enhanced', value: 'enhanced', label: 'Enhanced', color: 'text-purple-600' },
            { id: 'tier-filter-standard', value: 'standard', label: 'Standard', color: 'text-accent' },
            { id: 'tier-filter-free', value: 'free', label: 'Free', color: 'text-muted-foreground' },
          ].map((tier) => (
            <label
              key={tier.id}
              className="flex items-center gap-2.5 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedTiers.includes(tier.value)}
                onChange={() => toggleTier(tier.value)}
                className="accent-primary w-4 h-4"
              />
              <span className={`text-sm font-semibold ${tier.color}`}>{tier.label}</span>
            </label>
          ))}
        </div>
      </div>

      <hr className="border-border" />

      {/* Verified only */}
      <div>
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={verifiedOnly}
            onChange={(e) => setVerifiedOnly(e.target.checked)}
            className="accent-primary w-4 h-4"
          />
          <span className="text-sm font-semibold text-foreground">Council Verified Only</span>
        </label>
        <p className="text-xs text-muted-foreground mt-1 ml-6">
          Show only businesses verified by West Suffolk Council
        </p>
      </div>

      <button className="btn-secondary w-full text-sm justify-center">
        Clear All Filters
      </button>
    </div>
  );

  return (
    <>
      {/* Mobile filter toggle */}
      <div className="lg:hidden mb-4">
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="btn-secondary w-full justify-center"
        >
          <SlidersHorizontal size={15} />
          {mobileOpen ? 'Hide Filters' : 'Show Filters'}
        </button>
        {mobileOpen && (
          <div className="card-surface p-5 mt-3">
            {filterContent}
          </div>
        )}
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 xl:w-72 shrink-0">
        <div className="card-surface p-5 sticky top-32">
          <div className="flex items-center gap-2 mb-5">
            <SlidersHorizontal size={16} className="text-primary" />
            <h2 className="font-bold text-sm text-foreground">Filter Results</h2>
          </div>
          {filterContent}
        </div>
      </aside>
    </>
  );
}